import React, { useRef } from 'react';
import type { CatalogCurationHeaderFragment } from '@graphql/generated';
import {
  StyledBackgroundImage,
  StyledBackgroundVideo,
  StyledBackgroundVideoContainer,
  StyledBlockText,
  StyledButtonsContainer,
  StyledContainer,
  StyledContent,
  StyledDek,
  StyledDekMobile,
  StyledEyebrow,
  StyledGradient,
  StyledHed,
  StyledMediaContainer,
  StyledReadMoreButton,
  styles,
} from './CurationHero.styles';
import { BoxProps, Flex, IconV2 } from '@withjoy/joykit';
import { SkeletonGroup, SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { CurationVideoPlayer } from '../CurationVideoPlayer/CurationVideoPlayer';
import { useCurationHeroController } from './useCurationHeroController';
import { useTranslation } from '@shared/core';
import { ReactComponent as DownArrow } from '@assets/icons/down-chevron.svg';
import { useVimeoPlayerContext } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export type CurationHeroRootProps = {
  curationId: string;
  hero: CatalogCurationHeaderFragment;
  videoUrl: Maybe<string>;
  showSkeleton?: boolean;
};

const Container: React.FC<BoxProps> = ({ children, ...restProps }) => {
  return (
    <StyledContainer
      {...restProps}
      __css={styles.container}
    >
      {children}
    </StyledContainer>
  );
};

const CurationHeroRoot = (props: CurationHeroRootProps) => {
  const { title, backgroundImage, eyebrow, video, description } = props.hero;
  const { videoUrl } = props;

  const { isMobile, isDescriptionShown, setIsDescriptionShown, onWatchVideoCtaClicked } = useCurationHeroController(props);

  const { t } = useTranslation('catalogRegistry');
  const { readMore, less } = t('curationHero');
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const showReadMore = useRef<boolean>(false);
  if (descriptionRef.current && descriptionRef.current.scrollHeight - descriptionRef.current.offsetHeight > 5) {
    showReadMore.current = true;
  }

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { VimeoPlayer } = useVimeoPlayerContext();

  const handleOnCloseVideoDialog = useEventCallback(() => {
    VimeoPlayer && iframeRef.current && new VimeoPlayer(iframeRef.current).play();
  });

  return (
    <Container>
      <StyledContent __css={styles.content}>
        <StyledMediaContainer __css={styles.mediaContainer}>
          <StyledGradient __css={styles.gradient} />
          {video && (
            <StyledBackgroundVideo __css={styles.backgroundVideo}>
              <StyledBackgroundVideoContainer __css={styles.backgroundVideoContainer}>
                <iframe
                  ref={iframeRef}
                  src={`${video}&background=1`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`${eyebrow}-${title}`}
                />
              </StyledBackgroundVideoContainer>
            </StyledBackgroundVideo>
          )}
          {!video && backgroundImage?.url && <StyledBackgroundImage __css={styles.backgroundImage(backgroundImage.url)} />}
        </StyledMediaContainer>
        <StyledBlockText __css={styles.blockText}>
          {eyebrow && <StyledEyebrow __css={styles.eyebrow}>{eyebrow}</StyledEyebrow>}
          {title && (
            <StyledHed
              as="h1"
              __css={styles.hed}
            >
              {title}
            </StyledHed>
          )}
          {description &&
            (isMobile ? (
              <>
                <StyledDekMobile
                  as="p"
                  __css={styles.dek}
                  isDescriptionShown={isDescriptionShown}
                  ref={descriptionRef}
                >
                  {description}
                </StyledDekMobile>
                {showReadMore.current && (
                  <StyledReadMoreButton
                    as="button"
                    fontFamily="Inter UI"
                    typographyVariant="body1"
                    fontWeight={isDescriptionShown ? 'bold' : 'medium'}
                    onClick={() => setIsDescriptionShown(!isDescriptionShown)}
                  >
                    {isDescriptionShown ? (
                      less()
                    ) : (
                      <>
                        {readMore()}
                        <IconV2 padding="7px 0px 0px 5px">
                          <DownArrow />
                        </IconV2>
                      </>
                    )}
                  </StyledReadMoreButton>
                )}
              </>
            ) : (
              <StyledDek
                as="p"
                __css={styles.dek}
                color="white"
              >
                {description}
              </StyledDek>
            ))}
        </StyledBlockText>
        <StyledButtonsContainer __css={styles.buttonsContainer}>
          <Flex
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            {videoUrl && (
              <CurationVideoPlayer
                curationId={props.curationId}
                variant="default"
                videoUrl={videoUrl}
                eyebrow={eyebrow}
                title={title}
                handleOnViewCurationClick={null}
                onOpenVideoPlayer={onWatchVideoCtaClicked}
                onCloseVideoPlayer={handleOnCloseVideoDialog}
              />
            )}
          </Flex>
        </StyledButtonsContainer>
      </StyledContent>
    </Container>
  );
};

const CurationHero = (props: CurationHeroRootProps) => {
  const { showSkeleton: showSkeletonProp, hero, videoUrl } = props;

  if (!hero) {
    return null;
  }

  return (
    <SkeletonGroup
      isReady={!!hero && !showSkeletonProp}
      placeholder={
        <Container backgroundColor="mono1">
          <CurationHero.Skeleton />
        </Container>
      }
    >
      <CurationHeroRoot
        curationId={props.curationId}
        hero={hero}
        videoUrl={videoUrl}
      />
    </SkeletonGroup>
  );
};

CurationHero.Skeleton = () => {
  return (
    <StyledContainer __css={styles.container}>
      <StyledContent __css={styles.content}>
        <StyledButtonsContainer
          __css={styles.buttonsContainer}
          paddingBottom="55px"
        >
          <Flex
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            <SkeletonThumbnail
              variant="circle"
              height="92px"
              width="92px"
            />
          </Flex>
        </StyledButtonsContainer>
        <StyledBlockText __css={styles.blockText}>
          <SkeletonText
            rows={1}
            skeletonHeight={['30px', null, '35px']}
            width={['130px', null, '190px']}
            paddingBottom={6}
          />
          <SkeletonText
            rows={1}
            skeletonHeight={['120px', '60px', '85px']}
            width={['220px', '350px', '650px']}
            margin="15px"
          />
          <SkeletonText
            rows={1}
            skeletonHeight={['60px', null, '75px']}
            width={['330px', null, '630px']}
          />
        </StyledBlockText>
        <StyledButtonsContainer __css={styles.buttonsContainer}>
          <Flex
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            <SkeletonText
              rows={1}
              skeletonHeight={45}
              width="200px"
              margin={3}
            />
          </Flex>
        </StyledButtonsContainer>
      </StyledContent>
    </StyledContainer>
  );
};

export { CurationHero };
